import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useParams, Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const STATUS_BADGE = {
  not_started: 'bg-secondary',
  in_progress: 'bg-info text-dark',
  completed: 'bg-success',
  aborted: 'bg-dark',
};

const STATUS_ICON = {
  not_started: '⏳',
  in_progress: '▶️',
  completed: '✅',
  aborted: '🛑',
};

const PLATFORMS = ['iOS', 'Android', 'Web', 'Windows', 'macOS', 'Linux', 'Other'];
const ENVIRONMENTS = ['development', 'QA', 'staging', 'production', 'UAT'];

const DEFAULT_FORM = {
  name: '',
  description: '',
  runType: 'manual',
  assignedTo: '',
  // Configuration
  platform: '',
  deviceName: '',
  deviceModel: '',
  osVersion: '',
  appVersion: '',
  buildNumber: '',
  buildUrl: '',
  testMachine: '',
  browser: '',
  environment: '',
  // Test scope
  suiteIds: [],
  testCaseIds: [],
};

function TestRuns() {
  const [searchParams] = useSearchParams();
  const { projectKey: routeProjectKey } = useParams();
  const projectKey = routeProjectKey || searchParams.get('projectKey');
  const navigate = useNavigate();

  const [projectId, setProjectId] = useState(searchParams.get('projectId') || null);
  const [project, setProject] = useState(null);
  const [runs, setRuns] = useState([]);
  const [testSuites, setTestSuites] = useState([]);
  const [testCases, setTestCases] = useState([]);
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [sortOrder, setSortOrder] = useState('desc');

  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  // ── fetch project info ─────────────────────────────────────────────────────
  const fetchProject = useCallback(async () => {
    if (!projectKey) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/projects/${projectKey}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const p = data.data || data;
        if (p) { setProject(p); setProjectId(p.id || p._id); }
      }
    } catch (err) { console.error('Error loading project:', err); }
  }, [projectKey]);

  const fetchRuns = useCallback(async () => {
    try {
      setLoading(true);
      let url = `/runs?sortOrder=${sortOrder}`;
      if (filterStatus !== 'all') url += `&status=${filterStatus}`;
      if (filterType !== 'all') url += `&runType=${filterType}`;
      if (projectId) url += `&projectId=${projectId}`;

      const res = await api.get(url);
      setRuns(res.data.data || []);
      setError('');
    } catch (err) {
      setError('Failed to fetch test runs: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterType, sortOrder, projectId]);

  const fetchTestSuites = useCallback(async () => {
    try {
      let url = '/suites';
      if (projectId) url += `?projectId=${projectId}`;
      const res = await api.get(url);
      setTestSuites(res.data.data || res.data.testSuites || []);
    } catch (err) { console.error('Failed to fetch test suites:', err); }
  }, [projectId]);

  const fetchTestCases = useCallback(async () => {
    try {
      let url = '/testcases';
      if (projectId) url += `?projectId=${projectId}&limit=0`;
      const res = await api.get(url);
      setTestCases(res.data.data || res.data.testCases || []);
    } catch (err) { console.error('Failed to fetch test cases:', err); }
  }, [projectId]);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await api.get('/auth/users');
      setUsers(res.data || []);
    } catch { /* users list is optional */ }
  }, []);

  useEffect(() => { fetchProject(); }, [fetchProject]);

  // Fetch runs & suites whenever filters change OR once projectId is resolved.
  // On the global page (no projectKey) projectId stays null and we fetch immediately.
  // On a project page we wait until projectId is set by fetchProject.
  useEffect(() => {
    if (projectKey && projectId === null) return; // still resolving project
    fetchRuns();
    fetchTestSuites();
    fetchTestCases();
  }, [projectId, fetchRuns, fetchTestSuites, fetchTestCases]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // ── form helpers ───────────────────────────────────────────────────────────
  const setField = (key, val) => setFormData(prev => ({ ...prev, [key]: val }));

  const toggleSuite = (id) => {
    setFormData(prev => ({
      ...prev,
      suiteIds: prev.suiteIds.includes(id)
        ? prev.suiteIds.filter(s => s !== id)
        : [...prev.suiteIds, id],
    }));
  };

  const toggleTestCase = (id) => {
    setFormData(prev => ({
      ...prev,
      testCaseIds: prev.testCaseIds.includes(id)
        ? prev.testCaseIds.filter(t => t !== id)
        : [...prev.testCaseIds, id],
    }));
  };

  const selectAllTestCases = (ids) => {
    setFormData(prev => ({
      ...prev,
      testCaseIds: [...new Set([...prev.testCaseIds, ...ids])],
    }));
  };

  const clearAllTestCases = () => {
    setFormData(prev => ({ ...prev, testCaseIds: [] }));
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!formData.name.trim()) { setError('Run name is required'); return; }

    const pid = projectId || formData.projectId;
    if (!pid) { setError('Project is required. Open the run from a project page.'); return; }

    setCreating(true);
    setError('');
    try {
      const payload = {
        projectId: pid,
        name: formData.name.trim(),
        description: formData.description,
        runType: formData.runType,
        assignedTo: formData.assignedTo || undefined,
        suiteIds: formData.suiteIds,
        testCaseIds: formData.testCaseIds,
        configuration: {
          platform: formData.platform,
          deviceName: formData.deviceName,
          deviceModel: formData.deviceModel,
          osVersion: formData.osVersion,
          appVersion: formData.appVersion,
          buildNumber: formData.buildNumber,
          buildUrl: formData.buildUrl,
          testMachine: formData.testMachine,
          browser: formData.browser,
          environment: formData.environment,
        },
      };

      const res = await api.post('/runs', payload);
      setShowCreateModal(false);
      setFormData(DEFAULT_FORM);
      fetchRuns();
      // Navigate to the new run
      const newId = res.data._id || res.data.id;
      const base = projectKey ? `/projects/${projectKey}` : '';
      navigate(`${base}/runs/${newId}`);
    } catch (err) {
      setError('Failed to create test run: ' + (err.response?.data?.error || err.message));
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this test run? This cannot be undone.')) return;
    try {
      await api.delete(`/runs/${id}`);
      fetchRuns();
    } catch (err) {
      setError('Failed to delete: ' + (err.response?.data?.error || err.message));
    }
  };

  const getRunUrl = (run) => {
    const id = run._id || run.id;
    return projectKey ? `/projects/${projectKey}/runs/${id}` : `/runs/${id}`;
  };

  const getProgress = (run) => {
    if (!run.results?.length) return null;
    const total = run.results.length;
    const passed = run.results.filter(r => r.status === 'passed').length;
    const failed = run.results.filter(r => r.status === 'failed').length;
    const done = run.results.filter(r => ['passed', 'failed', 'blocked', 'skipped'].includes(r.status)).length;
    return { total, passed, failed, done, pct: Math.round((done / total) * 100) };
  };

  return (
    <div className="container mt-4">
      {/* Page header */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div>
          <h2 className="mb-0">
            {project ? `${project.name} – ` : ''}Test Runs
          </h2>
          <small className="text-muted">
            Manage manual and automated test run executions
          </small>
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
          + New Test Run
        </button>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError('')} />
        </div>
      )}

      {/* Filters */}
      <div className="card mb-3">
        <div className="card-body py-3">
          <div className="row g-2 align-items-center">
            <div className="col-xl-3 col-md-4">
              <div className="small text-muted mb-1">Status</div>
              <select className="form-select form-select-sm" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                <option value="all">All Status</option>
                <option value="not_started">Not Started</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="aborted">Aborted</option>
              </select>
            </div>
            <div className="col-xl-3 col-md-4">
              <div className="small text-muted mb-1">Type</div>
              <select className="form-select form-select-sm" value={filterType} onChange={e => setFilterType(e.target.value)}>
                <option value="all">All Types</option>
                <option value="manual">👤 Manual</option>
                <option value="automated">🤖 Automated</option>
              </select>
            </div>
            <div className="col-xl-3 col-md-4">
              <div className="small text-muted mb-1">Sort</div>
              <select className="form-select form-select-sm" value={sortOrder} onChange={e => setSortOrder(e.target.value)}>
                <option value="desc">Newest First</option>
                <option value="asc">Oldest First</option>
              </select>
            </div>
            <div className="col-xl-3 col-12 d-flex justify-content-xl-end align-items-end">
              <div className="small text-muted">{runs.length} run{runs.length !== 1 ? 's' : ''} shown</div>
            </div>
          </div>
        </div>
      </div>

      {/* Runs list */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border" role="status"><span className="visually-hidden">Loading…</span></div>
        </div>
      ) : runs.length === 0 ? (
        <div className="text-center py-5 text-muted">
          <div style={{ fontSize: '3rem' }}>🚀</div>
          <p>No test runs found. Create one to get started.</p>
          <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>+ New Test Run</button>
        </div>
      ) : (
        <div className="row g-3">
          {runs.map(run => {
            const progress = getProgress(run);
            const cfg = run.configuration || {};
            return (
              <div key={run._id || run.id} className="col-12">
                <div className="card">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                      <div className="flex-grow-1">
                        <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                          <Link to={getRunUrl(run)} className="fw-semibold text-decoration-none fs-6">
                            {run.name}
                          </Link>
                          <span className={`badge ${STATUS_BADGE[run.status] || 'bg-secondary'}`}>
                            {STATUS_ICON[run.status]} {run.status?.replace('_', ' ')}
                          </span>
                          <span className={`badge ${run.runType === 'automated' ? 'bg-primary' : 'bg-info text-dark'}`}>
                            {run.runType === 'automated' ? '🤖 Auto' : '👤 Manual'}
                          </span>
                        </div>

                        {/* Config pills */}
                        <div className="d-flex gap-2 flex-wrap" style={{ fontSize: '0.78rem' }}>
                          {cfg.platform && <span className="badge bg-light text-dark border">📱 {cfg.platform}</span>}
                          {cfg.deviceName && <span className="badge bg-light text-dark border">🔧 {cfg.deviceName}</span>}
                          {cfg.osVersion && <span className="badge bg-light text-dark border">⚙️ {cfg.osVersion}</span>}
                          {cfg.appVersion && <span className="badge bg-light text-dark border">📦 v{cfg.appVersion}</span>}
                          {cfg.environment && <span className="badge bg-light text-dark border">🌐 {cfg.environment}</span>}
                          {run.cicd?.jobName && <span className="badge bg-light text-dark border">🔨 {run.cicd.jobName} #{run.cicd.buildNumber}</span>}
                        </div>

                        <div className="mt-1 d-flex gap-3 flex-wrap" style={{ fontSize: '0.8rem', color: '#666' }}>
                          {run.assignedTo && <span>👤 {run.assignedTo.username || run.assignedTo}</span>}
                          <span>📅 {new Date(run.createdAt).toLocaleDateString()}</span>
                          {run.completedAt && <span>✓ {new Date(run.completedAt).toLocaleDateString()}</span>}
                        </div>
                      </div>

                      <div className="d-flex gap-2 align-items-center">
                        {/* Progress mini-bar */}
                        {progress && (
                          <div style={{ width: 100, textAlign: 'right' }}>
                            <div className="small text-muted mb-1">{progress.done}/{progress.total}</div>
                            <div className="progress" style={{ height: '6px', width: 100 }}>
                              <div className="progress-bar bg-success" style={{ width: `${(progress.passed / progress.total) * 100}%` }} />
                              <div className="progress-bar bg-danger" style={{ width: `${(progress.failed / progress.total) * 100}%` }} />
                            </div>
                          </div>
                        )}

                        <Link to={getRunUrl(run)} className="btn btn-sm btn-outline-primary">Open</Link>
                        <Link to={`${getRunUrl(run)}/results`} className="btn btn-sm btn-outline-secondary">Results</Link>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDelete(run._id || run.id)}
                          title="Delete run"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Create Run Modal (multi-step wizard) ────────────────────────────── */}
      {showCreateModal && (
        <CreateRunWizard
          formData={formData}
          setField={setField}
          toggleSuite={toggleSuite}
          testSuites={testSuites}
          testCases={testCases}
          toggleTestCase={toggleTestCase}
          selectAllTestCases={selectAllTestCases}
          clearAllTestCases={clearAllTestCases}
          users={users}
          creating={creating}
          error={error}
          onSubmit={handleSubmit}
          onClose={() => { setShowCreateModal(false); setError(''); setFormData(DEFAULT_FORM); }}
        />
      )}
    </div>
  );
}

// ── Multi-step Create Run Wizard ──────────────────────────────────────────────
const STEPS = [
  { id: 1, label: 'Basic Info',    icon: '📋' },
  { id: 2, label: 'Configuration', icon: '⚙️'  },
  { id: 3, label: 'Test Scope',    icon: '🧪' },
];

function StepIndicator({ current }) {
  return (
    <div className="d-flex align-items-center justify-content-center mb-4" style={{ gap: 0 }}>
      {STEPS.map((step, idx) => {
        const done    = current > step.id;
        const active  = current === step.id;
        return (
          <React.Fragment key={step.id}>
            <div className="d-flex flex-column align-items-center" style={{ minWidth: 80 }}>
              <div
                style={{
                  width: 38, height: 38, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: '0.85rem',
                  background: done ? '#0d6efd' : active ? '#0d6efd' : '#e9ecef',
                  color: done || active ? '#fff' : '#6c757d',
                  border: active ? '3px solid #84aef7' : '3px solid transparent',
                  transition: 'all 0.2s',
                  boxSizing: 'border-box',
                }}
              >
                {done ? '✓' : step.icon}
              </div>
              <div style={{
                fontSize: '0.7rem', marginTop: 4, fontWeight: active ? 700 : 400,
                color: active ? '#0d6efd' : done ? '#0d6efd' : '#6c757d',
              }}>
                {step.label}
              </div>
            </div>
            {idx < STEPS.length - 1 && (
              <div style={{
                flex: 1, height: 2, margin: '0 2px', marginBottom: 22,
                background: current > step.id ? '#0d6efd' : '#e9ecef',
                transition: 'background 0.2s',
              }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function CreateRunWizard({ formData, setField, toggleSuite, testSuites, testCases, toggleTestCase, selectAllTestCases, clearAllTestCases, users, creating, error, onSubmit, onClose }) {
  const [tcSearch, setTcSearch] = React.useState('');
  const [tcTab, setTcTab] = React.useState('suite'); // 'suite' | 'individual'
  const [step, setStep] = useState(1);
  const [stepError, setStepError] = useState('');

  const next = () => {
    if (step === 1 && !formData.name.trim()) {
      setStepError('Run name is required.');
      return;
    }
    setStepError('');
    setStep(s => s + 1);
  };

  const back = () => { setStepError(''); setStep(s => s - 1); };

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg" style={{ maxWidth: 640 }}>
        <div className="modal-content">
          {/* Header */}
          <div className="modal-header border-bottom-0 pb-0">
            <div>
              <h5 className="modal-title mb-0">🚀 New Test Run</h5>
              <small className="text-muted">Step {step} of {STEPS.length}</small>
            </div>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>

          {/* Body */}
          <div className="modal-body pt-3">
            <StepIndicator current={step} />

            {(stepError || error) && (
              <div className="alert alert-danger py-2 mb-3">{stepError || error}</div>
            )}

            {/* ── Step 1: Basic Info ── */}
            {step === 1 && (
              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label fw-semibold">Run Name <span className="text-danger">*</span></label>
                  <input
                    className="form-control"
                    value={formData.name}
                    onChange={e => setField('name', e.target.value)}
                    placeholder="e.g. Sprint 42 – iPhone regression"
                    autoFocus
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Run Type</label>
                  <div className="d-flex gap-2 mt-1">
                    {[['manual','👤 Manual'],['automated','🤖 Automated']].map(([val, lbl]) => (
                      <button
                        key={val} type="button"
                        className={`btn btn-sm flex-fill ${formData.runType === val ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setField('runType', val)}
                      >{lbl}</button>
                    ))}
                  </div>
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Assign To</label>
                  {users.length > 0 ? (
                    <select className="form-select" value={formData.assignedTo} onChange={e => setField('assignedTo', e.target.value)}>
                      <option value="">— Unassigned —</option>
                      {users.map(u => <option key={u._id || u.id} value={u._id || u.id}>{u.username}</option>)}
                    </select>
                  ) : (
                    <input className="form-control" value={formData.assignedTo} onChange={e => setField('assignedTo', e.target.value)} placeholder="Username or leave blank" />
                  )}
                </div>
                <div className="col-12">
                  <label className="form-label fw-semibold">Description</label>
                  <textarea className="form-control" rows={3} value={formData.description} onChange={e => setField('description', e.target.value)} placeholder="Optional notes about this run…" />
                </div>
              </div>
            )}

            {/* ── Step 2: Configuration ── */}
            {step === 2 && (
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Platform</label>
                  <select className="form-select" value={formData.platform} onChange={e => setField('platform', e.target.value)}>
                    <option value="">— Select —</option>
                    {PLATFORMS.map(p => <option key={p}>{p}</option>)}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Environment</label>
                  <select className="form-select" value={formData.environment} onChange={e => setField('environment', e.target.value)}>
                    <option value="">— Select —</option>
                    {ENVIRONMENTS.map(e => <option key={e}>{e}</option>)}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Device Name</label>
                  <input className="form-control" value={formData.deviceName} onChange={e => setField('deviceName', e.target.value)} placeholder="e.g. iPhone 15 Pro" />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Device Model</label>
                  <input className="form-control" value={formData.deviceModel} onChange={e => setField('deviceModel', e.target.value)} placeholder="e.g. A2650" />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">OS Version</label>
                  <input className="form-control" value={formData.osVersion} onChange={e => setField('osVersion', e.target.value)} placeholder="e.g. iOS 17.2" />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">App Version</label>
                  <input className="form-control" value={formData.appVersion} onChange={e => setField('appVersion', e.target.value)} placeholder="e.g. 2.4.1" />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Build Number</label>
                  <input className="form-control" value={formData.buildNumber} onChange={e => setField('buildNumber', e.target.value)} placeholder="e.g. 301" />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Test Machine</label>
                  <input className="form-control" value={formData.testMachine} onChange={e => setField('testMachine', e.target.value)} placeholder="e.g. macbook-pro-ci01" />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Browser <span className="text-muted fw-normal">(Web only)</span></label>
                  <input className="form-control" value={formData.browser} onChange={e => setField('browser', e.target.value)} placeholder="e.g. Chrome 121" />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Build URL</label>
                  <input type="url" className="form-control" value={formData.buildUrl} onChange={e => setField('buildUrl', e.target.value)} placeholder="https://ci.example.com/build/301" />
                </div>
              </div>
            )}

            {/* ── Step 3: Test Scope ── */}
            {step === 3 && (
              <>
                {/* Tab switcher */}
                <ul className="nav nav-pills mb-3" style={{ gap: 8 }}>
                  <li className="nav-item">
                    <button
                      className={`nav-link py-1 px-3 ${tcTab === 'suite' ? 'active' : ''}`}
                      onClick={() => setTcTab('suite')}
                    >📦 By Suite</button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link py-1 px-3 ${tcTab === 'individual' ? 'active' : ''}`}
                      onClick={() => setTcTab('individual')}
                    >📋 Individual Cases</button>
                  </li>
                </ul>

                {/* ── By Suite tab ── */}
                {tcTab === 'suite' && (
                  <>
                    <p className="text-muted small mb-2">All test cases in selected suites will be added.</p>
                    {testSuites.length === 0 ? (
                      <div className="text-center py-4 text-muted">
                        <div style={{ fontSize: '2rem' }}>📭</div>
                        <p className="mb-0">No test suites available for this project.</p>
                      </div>
                    ) : (
                      <div className="d-flex flex-column gap-2" style={{ maxHeight: 260, overflowY: 'auto' }}>
                        {testSuites.map(suite => {
                          const id = suite._id || suite.id;
                          const checked = formData.suiteIds.includes(id);
                          return (
                            <div
                              key={id}
                              className={`d-flex align-items-center gap-3 border rounded p-3 ${checked ? 'border-primary bg-primary bg-opacity-10' : 'bg-light'}`}
                              style={{ cursor: 'pointer', transition: 'all 0.15s' }}
                              onClick={() => toggleSuite(id)}
                            >
                              <input className="form-check-input mt-0 flex-shrink-0" type="checkbox"
                                checked={checked} onChange={() => toggleSuite(id)}
                                style={{ width: 18, height: 18 }} />
                              <div className="flex-grow-1">
                                <div className="fw-semibold">{suite.name}</div>
                                {suite.description && <div className="text-muted small">{suite.description}</div>}
                              </div>
                              {suite.testCaseCount != null && (
                                <span className="badge bg-secondary">{suite.testCaseCount} cases</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {formData.suiteIds.length > 0 && (() => {
                      const selectedSuites = testSuites.filter(s => formData.suiteIds.includes(s._id || s.id));
                      const totalTcCount = selectedSuites.reduce((sum, s) => sum + (s.testCaseCount || 0), 0);
                      return (
                        <div className="mt-2 alert alert-info py-2 mb-0">
                          ✓ <strong>{formData.suiteIds.length}</strong> suite{formData.suiteIds.length > 1 ? 's' : ''} selected
                          {totalTcCount > 0 && (
                            <span className="ms-2 text-muted">— <strong>{totalTcCount}</strong> test case{totalTcCount !== 1 ? 's' : ''} will be added</span>
                          )}
                        </div>
                      );
                    })()}
                  </>
                )}

                {/* ── Individual Cases tab ── */}
                {tcTab === 'individual' && (
                  <>
                    <div className="d-flex gap-2 align-items-center mb-2">
                      <input
                        className="form-control form-control-sm"
                        placeholder="🔍 Search test cases…"
                        value={tcSearch}
                        onChange={e => setTcSearch(e.target.value)}
                        style={{ flex: 1 }}
                      />
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary flex-shrink-0"
                        onClick={() => selectAllTestCases(testCases.map(tc => tc._id || tc.id))}
                        title="Select all test cases"
                      >Select All</button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary flex-shrink-0"
                        onClick={clearAllTestCases}
                        title="Deselect all"
                      >Clear</button>
                    </div>
                    {testCases.length === 0 ? (
                      <div className="text-center py-4 text-muted">
                        <div style={{ fontSize: '2rem' }}>📭</div>
                        <p className="mb-0">No test cases available for this project.</p>
                      </div>
                    ) : (
                      <div className="d-flex flex-column gap-1" style={{ maxHeight: 260, overflowY: 'auto' }}>
                        {testCases
                          .filter(tc => {
                            if (!tcSearch.trim()) return true;
                            const q = tcSearch.toLowerCase();
                            return (tc.title || '').toLowerCase().includes(q)
                              || (tc.testCaseId || '').toLowerCase().includes(q);
                          })
                          .map(tc => {
                            const id = tc._id || tc.id;
                            const checked = formData.testCaseIds.includes(id);
                            return (
                              <div
                                key={id}
                                className={`d-flex align-items-center gap-3 border rounded px-3 py-2 ${checked ? 'border-primary bg-primary bg-opacity-10' : 'bg-light'}`}
                                style={{ cursor: 'pointer', transition: 'all 0.12s' }}
                                onClick={() => toggleTestCase(id)}
                              >
                                <input className="form-check-input mt-0 flex-shrink-0" type="checkbox"
                                  checked={checked} onChange={() => toggleTestCase(id)}
                                  style={{ width: 16, height: 16 }} />
                                <div className="flex-grow-1 min-w-0">
                                  <div className="fw-semibold" style={{ fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {tc.title}
                                  </div>
                                  <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                                    {tc.testCaseId && <span className="me-2">{tc.testCaseId}</span>}
                                    {tc.suiteName && <span>📦 {tc.suiteName}</span>}
                                  </div>
                                </div>
                                {tc.priority && (
                                  <span className={`badge ${tc.priority === 'High' ? 'bg-danger' : tc.priority === 'Medium' ? 'bg-warning text-dark' : 'bg-secondary'}`}
                                    style={{ fontSize: '0.68rem' }}>{tc.priority}</span>
                                )}
                              </div>
                            );
                          })
                        }
                      </div>
                    )}
                    {formData.testCaseIds.length > 0 && (
                      <div className="mt-2 alert alert-info py-2 mb-0">
                        ✓ <strong>{formData.testCaseIds.length}</strong> test case{formData.testCaseIds.length > 1 ? 's' : ''} selected
                      </div>
                    )}
                  </>
                )}

                {/* Summary review */}
                <div className="mt-3 p-3 rounded border bg-light">
                  <div className="fw-semibold mb-2 text-muted" style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Review</div>
                  <div className="row g-1" style={{ fontSize: '0.88rem' }}>
                    <div className="col-5 text-muted">Run Name</div>
                    <div className="col-7 fw-semibold">{formData.name}</div>
                    <div className="col-5 text-muted">Type</div>
                    <div className="col-7">{formData.runType === 'automated' ? '🤖 Automated' : '👤 Manual'}</div>
                    {formData.platform && <><div className="col-5 text-muted">Platform</div><div className="col-7">{formData.platform}</div></>}
                    {formData.environment && <><div className="col-5 text-muted">Environment</div><div className="col-7">{formData.environment}</div></>}
                    {formData.appVersion && <><div className="col-5 text-muted">App Version</div><div className="col-7">v{formData.appVersion}</div></>}
                    {(formData.suiteIds.length > 0 || formData.testCaseIds.length > 0) && (() => {
                      const selectedSuites = testSuites.filter(s => formData.suiteIds.includes(s._id || s.id));
                      const suiteTcCount = selectedSuites.reduce((sum, s) => sum + (s.testCaseCount || 0), 0);
                      const totalTc = suiteTcCount + formData.testCaseIds.length;
                      return (
                        <><div className="col-5 text-muted">Scope</div>
                        <div className="col-7">
                          {formData.suiteIds.length > 0 && (
                            <div>{formData.suiteIds.length} suite{formData.suiteIds.length > 1 ? 's' : ''}
                              {suiteTcCount > 0 && <span className="text-muted ms-1">({suiteTcCount} cases)</span>}
                            </div>
                          )}
                          {formData.testCaseIds.length > 0 && (
                            <div>{formData.testCaseIds.length} individual case{formData.testCaseIds.length > 1 ? 's' : ''}</div>
                          )}
                          {totalTc > 0 && (
                            <div className="fw-semibold text-primary mt-1">Total: {totalTc} test case{totalTc !== 1 ? 's' : ''}</div>
                          )}
                        </div></>
                      );
                    })()}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="modal-footer">
            <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={creating}>
              Cancel
            </button>
            {step > 1 && (
              <button type="button" className="btn btn-secondary" onClick={back} disabled={creating}>
                ← Back
              </button>
            )}
            {step < STEPS.length ? (
              <button type="button" className="btn btn-primary" onClick={next}>
                Next →
              </button>
            ) : (
              <button type="button" className="btn btn-primary" onClick={onSubmit} disabled={creating}>
                {creating
                  ? <><span className="spinner-border spinner-border-sm me-2" />Creating…</>
                  : '🚀 Create Run'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default TestRuns;
